/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, XCircle, Music, RefreshCw } from 'lucide-react';
import { FretPosition } from '../Fretboard';
import { QuizScore } from './FretboardQuiz';

interface IntervalRecognitionQuizProps {
  tuning: string[];
  fretRange: { start: number; end: number };
  setFretRange?: (range: { start: number; end: number }) => void;
  userFretboardAnswers: FretPosition[];
  setUserFretboardAnswers: (answers: FretPosition[]) => void;
  setHighlightedFretPositions: (positions: FretPosition[]) => void;
  correctUserFretboardAnswers: FretPosition[];
  setCorrectUserFretboardAnswers: (positions: FretPosition[]) => void;
  incorrectUserFretboardAnswers: FretPosition[];
  setIncorrectUserFretboardAnswers: (positions: FretPosition[]) => void;
  setRootNotePosition: (position: FretPosition | null) => void;
  updateScore: (correct: number, total: number, streak: number) => void;
  currentScore: QuizScore;
}

interface Interval {
  name: string;
  shortName: string;
  semitones: number;
  color: string;
}

interface IntervalQuestion {
  rootNote: string;
  rootPosition: FretPosition;
  targetInterval: Interval;
  questionType: 'findInterval';
  correctPositions: FretPosition[];
}

const IntervalRecognitionQuiz: React.FC<IntervalRecognitionQuizProps> = ({
  tuning,
  fretRange,
  setFretRange,
  userFretboardAnswers,
  setUserFretboardAnswers,
  setHighlightedFretPositions,
  correctUserFretboardAnswers,
  setCorrectUserFretboardAnswers,
  incorrectUserFretboardAnswers,
  setIncorrectUserFretboardAnswers,
  setRootNotePosition,
  updateScore,
  currentScore
}) => {
  const [currentQuestion, setCurrentQuestion] = useState<IntervalQuestion | null>(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [feedback, setFeedback] = useState<'correct' | 'incorrect' | null>(null);
  const [immediateFeedback, setImmediateFeedback] = useState<{position: FretPosition, isCorrect: boolean} | null>(null);
  const [localCorrectAnswers, setLocalCorrectAnswers] = useState<FretPosition[]>([]);
  const [localIncorrectAnswers, setLocalIncorrectAnswers] = useState<FretPosition[]>([]);

  // Musical intervals with their properties
  const intervals: Interval[] = [
    { name: 'Minor 2nd', shortName: 'm2', semitones: 1, color: '#ef4444' },
    { name: 'Major 2nd', shortName: 'M2', semitones: 2, color: '#f97316' },
    { name: 'Minor 3rd', shortName: 'm3', semitones: 3, color: '#eab308' },
    { name: 'Major 3rd', shortName: 'M3', semitones: 4, color: '#22c55e' },
    { name: 'Perfect 4th', shortName: 'P4', semitones: 5, color: '#06b6d4' },
    { name: 'Tritone', shortName: 'TT', semitones: 6, color: '#8b5cf6' },
    { name: 'Perfect 5th', shortName: 'P5', semitones: 7, color: '#ec4899' },
    { name: 'Minor 6th', shortName: 'm6', semitones: 8, color: '#f59e0b' },
    { name: 'Major 6th', shortName: 'M6', semitones: 9, color: '#10b981' },
    { name: 'Minor 7th', shortName: 'm7', semitones: 10, color: '#3b82f6' },
    { name: 'Major 7th', shortName: 'M7', semitones: 11, color: '#6366f1' },
    { name: 'Octave', shortName: '8va', semitones: 12, color: '#059669' },
  ];

  // Basic intervals for beginners
  const basicIntervals = intervals.filter(interval => 
    ['M3', 'P4', 'P5', 'm3', 'M2', '8va'].includes(interval.shortName)
  );

  // All chromatic notes
  const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

  // Calculate what note is at a specific fret on a string
  const calculateNoteAtFret = (openNote: string, fret: number): string => {
    const flatToSharp: { [key: string]: string } = {
      'Db': 'C#', 'Eb': 'D#', 'Gb': 'F#', 'Ab': 'G#', 'Bb': 'A#'
    };
    
    const normalizedOpenNote = flatToSharp[openNote] || openNote;
    const openIndex = notes.indexOf(normalizedOpenNote);
    
    if (openIndex === -1) return openNote;
    
    const targetIndex = (openIndex + fret) % 12;
    return notes[targetIndex];
  };

  // Calculate the target note for a given interval from a root note
  const calculateIntervalNote = (rootNote: string, semitones: number): string => {
    const flatToSharp: { [key: string]: string } = {
      'Db': 'C#', 'Eb': 'D#', 'Gb': 'F#', 'Ab': 'G#', 'Bb': 'A#'
    };
    
    const normalizedRootNote = flatToSharp[rootNote] || rootNote;
    const rootIndex = notes.indexOf(normalizedRootNote);
    
    if (rootIndex === -1) return rootNote;
    
    const targetIndex = (rootIndex + semitones) % 12;
    return notes[targetIndex];
  };

  // Find all positions of a specific note within a limited fret range
  const findNotePositionsInRange = (targetNote: string, limitedRange: { start: number; end: number }): FretPosition[] => {
    const positions: FretPosition[] = [];

    for (let stringIndex = 0; stringIndex < tuning.length; stringIndex++) {
      for (let fret = limitedRange.start; fret <= limitedRange.end; fret++) {
        const noteAtFret = calculateNoteAtFret(tuning[stringIndex], fret);
        if (noteAtFret === targetNote) {
          positions.push({
            string: stringIndex + 1,
            fret: fret,
            note: targetNote
          });
        }
      }
    }

    return positions;
  };

  // Find a single target position for the interval within the limited range
  const findSingleTargetPosition = (targetNote: string, limitedRange: { start: number; end: number }): FretPosition | null => {
    const positions = findNotePositionsInRange(targetNote, limitedRange);
    if (positions.length === 0) return null;
    
    // Return a random position from the available ones
    return positions[Math.floor(Math.random() * positions.length)];
  };

  // Get a random root position on the fretboard
  const getRandomRootPosition = (): FretPosition => {
    const stringIndex = Math.floor(Math.random() * tuning.length);
    const fret = Math.floor(Math.random() * (fretRange.end - fretRange.start + 1)) + fretRange.start;
    const note = calculateNoteAtFret(tuning[stringIndex], fret);
    
    return {
      string: stringIndex + 1,
      fret: fret,
      note: note
    };
  };

  // Generate a new interval question with limited fret range
  const generateNewQuestion = () => {
    let attempts = 0;
    const maxAttempts = 20;
    
    while (attempts < maxAttempts) {
      // Get random root position
      const rootPosition = getRandomRootPosition();
      
      // Create limited fret range (AT LEAST 3 frets below and above the root)
      const minRange = 3; // Minimum frets below and above
      let rangeStart = Math.max(fretRange.start, rootPosition.fret - minRange);
      let rangeEnd = Math.min(fretRange.end, rootPosition.fret + minRange);
      
      // Ensure we have AT LEAST 3 frets above and below when possible
      const actualBelow = rootPosition.fret - rangeStart;
      const actualAbove = rangeEnd - rootPosition.fret;
      
      // If we don't have enough frets below, extend above (and vice versa)
      if (actualBelow < minRange && rangeEnd < fretRange.end) {
        const needed = minRange - actualBelow;
        rangeEnd = Math.min(fretRange.end, rangeEnd + needed);
      }
      if (actualAbove < minRange && rangeStart > fretRange.start) {
        const needed = minRange - actualAbove;
        rangeStart = Math.max(fretRange.start, rangeStart - needed);
      }
      
      const limitedRange = {
        start: rangeStart,
        end: rangeEnd
      };
      
      // Choose a random interval (start with basic intervals for easier learning)
      const availableIntervals = currentScore.total < 10 ? basicIntervals : intervals;
      
      // Try different intervals in random order to find one that works
      const shuffledIntervals = [...availableIntervals].sort(() => Math.random() - 0.5);
      
      for (const targetInterval of shuffledIntervals) {
        // Calculate target note
        const targetNote = calculateIntervalNote(rootPosition.note, targetInterval.semitones);
        
        // Find a single target position within the limited range
        const targetPosition = findSingleTargetPosition(targetNote, limitedRange);
        
        // If we found a valid target position, use this interval
        if (targetPosition) {
          // Get ALL positions of the target note within the limited range
          const allTargetPositions = findNotePositionsInRange(targetNote, limitedRange);
          
          const newQuestion: IntervalQuestion = {
            rootNote: rootPosition.note,
            rootPosition: rootPosition,
            targetInterval: targetInterval,
            questionType: 'findInterval',
            correctPositions: allTargetPositions, // Accept all valid positions
            targetPosition: targetPosition, // Keep one for display purposes
            limitedFretRange: limitedRange
          };

          setCurrentQuestion(newQuestion);
          setShowAnswer(false);
          setFeedback(null);
          
          // Clear previous feedback after a brief delay
          setTimeout(() => {
            setImmediateFeedback(null);
          }, 100);
          
          // Clear all fretboard state
          setUserFretboardAnswers([]);
          setHighlightedFretPositions([]);
          setCorrectUserFretboardAnswers([]);
          setIncorrectUserFretboardAnswers([]);
          setLocalCorrectAnswers([]);
          setLocalIncorrectAnswers([]);
          
          // Set the root note position to highlight it on the fretboard
          setRootNotePosition(rootPosition);
          
          // Update the fretboard's visible range to the limited range
          if (setFretRange) {
            setFretRange(limitedRange);
          }
          
          return; // Successfully generated question
        }
      }
      
      attempts++;
    }
    
    // Fallback: if we can't find a valid position after many attempts, use the original logic
    console.warn('Could not generate question with limited range, falling back to original logic');
    const rootPosition = getRandomRootPosition();
    const availableIntervals = currentScore.total < 10 ? basicIntervals : intervals;
    const targetInterval = availableIntervals[Math.floor(Math.random() * availableIntervals.length)];
    const targetNote = calculateIntervalNote(rootPosition.note, targetInterval.semitones);
    const allPositions = findNotePositionsInRange(targetNote, fretRange);
    
    if (allPositions.length > 0) {
      const targetPosition = allPositions[0];
      // Create limited fret range (AT LEAST 3 frets below and above the root)
      const minRange = 3; // Minimum frets below and above
      let rangeStart = Math.max(fretRange.start, rootPosition.fret - minRange);
      let rangeEnd = Math.min(fretRange.end, rootPosition.fret + minRange);
      
      // Ensure we have AT LEAST 3 frets above and below when possible
      const actualBelow = rootPosition.fret - rangeStart;
      const actualAbove = rangeEnd - rootPosition.fret;
      
      // If we don't have enough frets below, extend above (and vice versa)
      if (actualBelow < minRange && rangeEnd < fretRange.end) {
        const needed = minRange - actualBelow;
        rangeEnd = Math.min(fretRange.end, rangeEnd + needed);
      }
      if (actualAbove < minRange && rangeStart > fretRange.start) {
        const needed = minRange - actualAbove;
        rangeStart = Math.max(fretRange.start, rangeStart - needed);
      }
      
      const limitedRange = {
        start: rangeStart,
        end: rangeEnd
      };
      
      // Get all positions within the limited range
      const limitedPositions = findNotePositionsInRange(targetNote, limitedRange);
      
      const newQuestion: IntervalQuestion = {
        rootNote: rootPosition.note,
        rootPosition: rootPosition,
        targetInterval: targetInterval,
        questionType: 'findInterval',
        correctPositions: limitedPositions.length > 0 ? limitedPositions : allPositions,
        targetPosition: targetPosition,
        limitedFretRange: limitedRange
      };

      setCurrentQuestion(newQuestion);
      setShowAnswer(false);
      setFeedback(null);
      setUserFretboardAnswers([]);
      setHighlightedFretPositions([]);
      setCorrectUserFretboardAnswers([]);
      setIncorrectUserFretboardAnswers([]);
      setLocalCorrectAnswers([]);
      setLocalIncorrectAnswers([]);
      setRootNotePosition(rootPosition);
      
      if (setFretRange) {
        setFretRange(limitedRange);
      }
    }
  };

  // Check user's answer
  const checkAnswer = () => {
    if (!currentQuestion || userFretboardAnswers.length === 0) return;

    // Check if user found at least one correct position
    const foundCorrectCount = userFretboardAnswers.filter(userPos =>
      currentQuestion.correctPositions.some(correctPos =>
        correctPos.string === userPos.string && correctPos.fret === userPos.fret
      )
    ).length;

    const isCorrect = foundCorrectCount > 0;

    setFeedback(isCorrect ? 'correct' : 'incorrect');
    setShowAnswer(true);
    setHighlightedFretPositions(currentQuestion.correctPositions);

    // Update score
    const newTotal = currentScore.total + 1;
    const newCorrect = currentScore.correct + (isCorrect ? 1 : 0);
    const newStreak = isCorrect ? currentScore.streak + 1 : 0;
    updateScore(newCorrect, newTotal, newStreak);
  };

  // Next question
  const nextQuestion = () => {
    // If there's a current question and the user has made attempts, check the answer first
    if (currentQuestion && userFretboardAnswers.length > 0 && !feedback) {
      checkAnswer();
      
      // Generate new question after score is updated
      setTimeout(() => {
        generateNewQuestion();
      }, 50);
    } else {
      generateNewQuestion();
    }
  };

  // Handle show answer button click
  const handleShowAnswer = () => {
    if (!currentQuestion) return;
    
    setHighlightedFretPositions(currentQuestion.correctPositions);
    setCorrectUserFretboardAnswers([]);
    setIncorrectUserFretboardAnswers([]);
  };

  // Generate first question on mount
  useEffect(() => {
    generateNewQuestion();
  }, []);

  // Check for immediate feedback when user clicks a fret
  useEffect(() => {
    if (!currentQuestion) return;
    
    if (userFretboardAnswers.length === 0) {
      setImmediateFeedback(null);
      setLocalCorrectAnswers([]);
      setLocalIncorrectAnswers([]);
      return;
    }

    const lastAnswer = userFretboardAnswers[userFretboardAnswers.length - 1];
    const isCorrect = currentQuestion.correctPositions.some(correctPos =>
      correctPos.string === lastAnswer.string && correctPos.fret === lastAnswer.fret
    );

    setImmediateFeedback({ position: lastAnswer, isCorrect });
    
    // Update local answers arrays for UI display
    const correctUserAnswers = userFretboardAnswers.filter(userPos =>
      currentQuestion.correctPositions.some(correctPos =>
        correctPos.string === userPos.string && correctPos.fret === userPos.fret
      )
    );
    const incorrectUserAnswers = userFretboardAnswers.filter(userPos =>
      !currentQuestion.correctPositions.some(correctPos =>
        correctPos.string === userPos.string && correctPos.fret === userPos.fret
      )
    );
    
    setLocalCorrectAnswers(correctUserAnswers);
    setLocalIncorrectAnswers(incorrectUserAnswers);
    setCorrectUserFretboardAnswers(correctUserAnswers);
    setIncorrectUserFretboardAnswers(incorrectUserAnswers);
  }, [userFretboardAnswers, currentQuestion]);

  // Get question text
  const getQuestionText = (): string => {
    if (!currentQuestion) return '';
    return `Find the ${currentQuestion.targetInterval.name} from ${currentQuestion.rootNote}`;
  };

  // Get instructions text
  const getInstructionsText = (): string => {
    return 'The root note is highlighted. Click on positions that are the requested interval from the root.';
  };

  if (!currentQuestion) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p>Loading question...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Question Display */}
      <Card className="p-4">
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-2">
            <Music className="h-5 w-5 text-purple-600" />
            <h3 className="text-lg font-semibold">{getQuestionText()}</h3>
          </div>
          <p className="text-sm text-muted-foreground">{getInstructionsText()}</p>
          
          {/* Interval Info */}
          <div className="flex items-center justify-center gap-4 text-xs">
            <div className="flex items-center gap-1">
              <span className="font-medium">Root:</span>
              <Badge variant="outline" className="bg-blue-50">
                {currentQuestion.rootNote} (S{currentQuestion.rootPosition.string}F{currentQuestion.rootPosition.fret})
              </Badge>
            </div>
            <div className="flex items-center gap-1">
              <span className="font-medium">Interval:</span>
              <Badge 
                variant="outline" 
                style={{ backgroundColor: currentQuestion.targetInterval.color + '20', borderColor: currentQuestion.targetInterval.color }}
              >
                {currentQuestion.targetInterval.shortName} ({currentQuestion.targetInterval.semitones} semitones)
              </Badge>
            </div>
          </div>
        </div>
      </Card>

      {/* User Progress */}
      {!showAnswer && (
        <Card className="p-3 bg-purple-50 border-purple-200">
          <div className="text-center space-y-2">
            <p className="text-sm text-purple-800 font-medium">
              {localCorrectAnswers.length > 0 
                ? `✓ Correct! ${currentQuestion.correctPositions.length > 1 
                    ? `(${currentQuestion.correctPositions.length - localCorrectAnswers.length} additional positions available)` 
                    : ''}`
                : `Looking for ${currentQuestion.correctPositions.length} position${currentQuestion.correctPositions.length !== 1 ? 's' : ''}`
              }
            </p>
            
            {/* Display user selections */}
            {userFretboardAnswers.length > 0 && (
              <div className="text-xs text-purple-700 space-y-1">
                <p className="font-medium">Your selections:</p>
                <div className="flex flex-wrap justify-center gap-1">
                  {userFretboardAnswers.map((answer, index) => {
                    const isCorrect = currentQuestion.correctPositions.some(correctPos =>
                      correctPos.string === answer.string && correctPos.fret === answer.fret
                    );
                    return (
                      <Badge 
                        key={index} 
                        variant={isCorrect ? "default" : "destructive"}
                        className={`text-xs ${
                          isCorrect 
                            ? 'bg-green-100 text-green-800 border-green-300' 
                            : 'bg-red-100 text-red-800 border-red-300'
                        }`}
                      >
                        S{answer.string}F{answer.fret}
                      </Badge>
                    );
                  })}
                </div>
                <p className="text-xs">
                  <span className="text-green-700">{localCorrectAnswers.length} correct</span>
                  {localIncorrectAnswers.length > 0 && (
                    <span>, <span className="text-red-700">{localIncorrectAnswers.length} incorrect</span></span>
                  )}
                </p>
              </div>
            )}
            
            <div className="flex flex-wrap justify-center gap-1 mt-2">
              {userFretboardAnswers.length > 0 && (
                <Button size="sm" onClick={checkAnswer}>
                  Submit Answer
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={nextQuestion}>
                Next Question
              </Button>
              <Button size="sm" variant="outline" onClick={handleShowAnswer}>
                Show Answer
              </Button>
              {userFretboardAnswers.length > 0 && (
                <Button size="sm" variant="outline" onClick={() => {
                  setUserFretboardAnswers([]);
                  setCorrectUserFretboardAnswers([]);
                  setIncorrectUserFretboardAnswers([]);
                  setHighlightedFretPositions([]);
                  setLocalCorrectAnswers([]);
                  setLocalIncorrectAnswers([]);
                  setImmediateFeedback(null);
                }}>
                  Clear All
                </Button>
              )}
            </div>
            
            {userFretboardAnswers.length === 0 && (
              <p className="text-xs text-purple-600 mt-2">
                Click on the fretboard to find all {currentQuestion.targetInterval.name} positions from the highlighted root note
              </p>
            )}
          </div>
        </Card>
      )}

      {/* Answer Feedback */}
      {showAnswer && (
        <Card className={`p-4 ${
          feedback === 'correct' 
            ? 'bg-green-50 border-green-200' 
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="text-center space-y-3">
            <div className="flex items-center justify-center gap-2">
              {feedback === 'correct' ? (
                <CheckCircle className="h-6 w-6 text-green-600" />
              ) : (
                <XCircle className="h-6 w-6 text-red-600" />
              )}
              <span className={`font-semibold ${
                feedback === 'correct' ? 'text-green-800' : 'text-red-800'
              }`}>
                {feedback === 'correct' ? 'Correct!' : 'Not quite...'}
              </span>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm">
                Found {currentQuestion.correctPositions.length} position{currentQuestion.correctPositions.length !== 1 ? 's' : ''} for {currentQuestion.targetInterval.name} from {currentQuestion.rootNote}
              </p>
              <div className="text-xs text-muted-foreground">
                All correct positions are highlighted on the fretboard
              </div>
            </div>

            <Button onClick={nextQuestion} className="mt-4">
              <RefreshCw className="h-4 w-4 mr-2" />
              Next Question
            </Button>
          </div>
        </Card>
      )}

      {/* Quick Start */}
      {userFretboardAnswers.length === 0 && !showAnswer && (
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="text-center space-y-2">
            <p className="text-sm text-blue-800 font-medium">Click on the fretboard to start!</p>
            <p className="text-xs text-blue-600">
              The root note ({currentQuestion.rootNote}) is highlighted. Find any position that is a {currentQuestion.targetInterval.name} away from it.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
};

export default IntervalRecognitionQuiz;